/**
 * An item in a history stack
 * @class
 */
export class HistoryItem {
	/**
	 * @constructor
	 * @param {String} name
	 * @param {Function} redo
	 * @param {Function} undo
	 */
	constructor(name, redo, undo) {
		if (!name.trim && !name.trim()) throw new Error('Expected parameter "name" to be a non-empty string');
		if (!(redo instanceof Function))
			throw new TypeError(`Expected parameter "redo" to be of type Function instead got "${typeof redo}"`);
		if (!(undo instanceof Function))
			throw new TypeError(`Expected parameter "undo" to be of type Function instead got "${typeof undo}"`);

		/** @member {String} */
		this.name = name;
		/** @member {Function} */
		this.redo = redo;
		/** @member {Function} */
		this.undo = undo;
	}
}

/**
 * History stack
 * @class
 * @extends Array
 */
export class HistoryStack {
	/**
	 * Use goto method to jump to a specific index
	 * @member {Number}
	 * @readonly
	 */
	index = -1;

	/**
	 * @member {HistoryItem[]}
	 * @readonly
	 */
	stack = [];

	/**
	 * Replay / Rewind history to given index
	 * @param {Number} targetIndex
	 */
	goto(targetIndex) {
		if (Number.isNaN(targetIndex)) throw new TypeError('Invalid index given');
		targetIndex = Math.min(Math.max(targetIndex, 0), this.stack.length);
		if (targetIndex < this.index) while (this.index > targetIndex) this.stack[--this.index].redo();
		else while (targetIndex > this.index) this.stack[this.index++].undo();
	}

	/** rewind history by one step */
	undo() {
		return this.goto(this.index + 1);
	}

	/** replay history by one step */
	redo() {
		return this.goto(this.index - 1);
	}

	/**
	 * Push history items to the stack
	 * @param {...HistoryItemInterface} items in the order of [add A, add B, A+B]
	 */
	push(...items) {
		if (items.some((item) => !(item.undo instanceof Function && item.redo instanceof Function))) {
			throw new TypeError('Expected given items to implement HistoryItemInterface');
		}
		// purge detached history elements
		for (; this.index > 0; this.index--) this.stack.shift();
		this.stack.unshift(...items.reverse());
		this.index = 0;
	}

	/**
	 * Clear history, remove all items from history
	 */
	clear() {
		this.index = -1;
		this.stack.length = 0;
	}
}
